import { useCallback, useMemo } from 'react';

import { Input } from '@/components/core/input';
import { useDebouncedControlledState } from '@/shared/hooks/use-debounced-controlled-state';

import { type Filter, type FilterDefs, filterValueSchemas, type UnsafeFilter } from './types';

type Props = {
  filter: Filter<FilterDefs, string>;
  onChange: (filter: UnsafeFilter) => void;
};

function parseValue(value: unknown): number | undefined {
  const parsedValue = filterValueSchemas.number.safeParse(value);
  if (!parsedValue.success) {
    return undefined;
  }
  return parsedValue.data;
}

export function DataTableNumberInput({ filter, onChange }: Props) {
  const [value, setValue] = useDebouncedControlledState({
    value: useMemo(() => parseValue(filter.value), [filter.value]),
    onChange: useCallback(
      (newValue: number | undefined) => {
        onChange({
          id: filter.id,
          operator: filter.operator,
          value: newValue,
        });
      },
      [filter.id, filter.operator, onChange],
    ),
  });

  return (
    <Input
      id={filter.id}
      type="number"
      inputMode="numeric"
      placeholder="Value..."
      className="h-full w-24 rounded-none px-1.5"
      value={value ?? ''}
      onChange={(event) =>
        setValue(
          event.currentTarget.value === ''
            ? undefined
            : parseValue(event.currentTarget.valueAsNumber),
        )
      }
    />
  );
}
